import {
  classifyCodexAxonEvent,
  isCodexAgentMessageDeltaEvent,
  isCodexApprovalRequestEvent,
  isCodexCommandExecutionOutputDeltaEvent,
  isCodexErrorEvent,
  isCodexItemCompletedEvent,
  isCodexItemStartedEvent,
  isCodexReasoningSummaryPartAddedEvent,
  isCodexReasoningSummaryTextDeltaEvent,
  isCodexReasoningTextDeltaEvent,
  isCodexTurnCompletedEvent,
  isCodexTurnStartedEvent,
  type CodexTimelineEvent,
  type ServerNotification,
} from '@runloop/remote-agents-sdk/codex';
import type { AxonEventView } from '@runloop/remote-agents-sdk/shared';
import type { AxonEvent } from '@reflex/shared';
import type {
  AvailableCommand,
  ChatItem,
  ChatTokenUsage,
  CodexInitExtensions,
  ContentItem,
  TaskBlock,
  ToolCallBlock,
  ToolCallStatus,
} from './types.js';
import type { BlockManager } from './useBlockManager.js';
import {
  CODEX_COMMAND_RESULT_EVENT_TYPE,
  CODEX_THREAD_START_EVENT_TYPE,
  CODEX_TOKEN_USAGE_EVENT_TYPE,
} from './timeline-helpers.js';
import { durationSecs, inferToolKind, nextBlockId } from './parsers.js';

type ThreadItem = Extract<ServerNotification, { method: 'item/started' }>['params']['item'];

type CodexToolItem = Extract<
  ThreadItem,
  { type: 'commandExecution' | 'fileChange' | 'mcpToolCall' | 'webSearch' }
>;

type CodexSubAgentActivityItem = Extract<ThreadItem, { type: 'subAgentActivity' }>;
type CodexCollabAgentToolCallItem = Extract<ThreadItem, { type: 'collabAgentToolCall' }>;
type UpdateFlushedTask = (taskId: string, update: Partial<TaskBlock>) => void;
type UpdateFlushedToolCall = (
  toolCallId: string,
  update: Partial<Pick<ToolCallBlock, 'status' | 'duration' | 'extra' | 'content' | 'rawOutput'>>,
  eventTimestamp: number,
) => void;

function backgroundAgentName(agentPath: string): string {
  const leaf = agentPath.split('/').filter(Boolean).at(-1) ?? '';
  const words = leaf.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Background agent';
}

function collabStatus(status: string): TaskBlock['status'] {
  switch (status) {
    case 'completed':
      return 'completed';
    case 'errored':
      return 'failed';
    case 'interrupted':
    case 'shutdown':
    case 'notFound':
      return 'stopped';
    case 'pendingInit':
    case 'running':
    default:
      return 'in_progress';
  }
}

function updateSubagentTask(
  taskId: string,
  update: Partial<TaskBlock>,
  blocks: BlockManager,
  updateFlushedTask?: UpdateFlushedTask,
): void {
  const hasLiveTask = blocks.blocksRef.current.some(
    (block) => block.type === 'task' && block.taskId === taskId,
  );
  if (hasLiveTask) {
    blocks.updateBlocks((current) =>
      current.map((block) =>
        block.type === 'task' && block.taskId === taskId ? { ...block, ...update } : block,
      ),
    );
  } else {
    updateFlushedTask?.(taskId, update);
  }
}

function registerSubagent(
  threadId: string,
  options: {
    agentPath?: string;
    prompt?: string | null;
    model?: string | null;
    status?: TaskBlock['status'];
  },
  blocks: BlockManager,
  sourceEvent: AxonEvent,
  updateFlushedTask?: UpdateFlushedTask,
): void {
  const existing = blocks.codexSubagentsRef.current.get(threadId);
  const agentPath = options.agentPath ?? existing?.agentPath ?? '';
  const taskUpdate: Partial<TaskBlock> = {
    description: backgroundAgentName(agentPath),
    status: options.status ?? 'in_progress',
    agentName: 'Codex',
    agentPath: agentPath || undefined,
    ...(options.model != null ? { model: options.model } : {}),
    ...(options.prompt != null ? { prompt: options.prompt } : {}),
  };

  if (existing) {
    blocks.codexSubagentsRef.current.set(threadId, { ...existing, agentPath });
    updateSubagentTask(existing.taskId, taskUpdate, blocks, updateFlushedTask);
    return;
  }

  blocks.codexSubagentsRef.current.set(threadId, { taskId: threadId, agentPath, toolUses: 0 });
  blocks.finalizeThinking(sourceEvent.timestamp);
  blocks.pushBlock({
    type: 'task',
    id: nextBlockId('task'),
    taskId: threadId,
    description: taskUpdate.description ?? 'Background agent',
    status: taskUpdate.status ?? 'in_progress',
    agentName: 'Codex',
    agentPath: taskUpdate.agentPath,
    model: taskUpdate.model,
    prompt: taskUpdate.prompt,
    toolUses: 0,
    sourceEvent,
    extra: { protocol: 'codex', agentThreadId: threadId },
  });
}

function handleSubAgentActivity(
  item: CodexSubAgentActivityItem,
  blocks: BlockManager,
  sourceEvent: AxonEvent,
  updateFlushedTask?: UpdateFlushedTask,
): void {
  // Collaboration activity is bidirectional. When a child interacts with the
  // orchestrator, Codex emits an activity item that names the root thread as
  // the other participant. Root is the visible conversation, not a background
  // agent; registering it here would absorb its later final answer into a task.
  if (item.agentPath === '/root') return;

  registerSubagent(
    item.agentThreadId,
    {
      agentPath: item.agentPath,
      status: item.kind === 'interrupted' ? 'stopped' : 'in_progress',
    },
    blocks,
    sourceEvent,
    updateFlushedTask,
  );
}

function handleCollabAgentToolCall(
  item: CodexCollabAgentToolCallItem,
  blocks: BlockManager,
  sourceEvent: AxonEvent,
  updateFlushedTask?: UpdateFlushedTask,
): void {
  for (const threadId of item.receiverThreadIds) {
    registerSubagent(
      threadId,
      { prompt: item.prompt, model: item.model },
      blocks,
      sourceEvent,
      updateFlushedTask,
    );
  }

  for (const [threadId, state] of Object.entries(item.agentsStates)) {
    if (!state) continue;
    registerSubagent(
      threadId,
      { model: item.model, prompt: item.prompt, status: collabStatus(state.status) },
      blocks,
      sourceEvent,
      updateFlushedTask,
    );
    updateSubagentTask(
      threadId,
      { status: collabStatus(state.status), summary: state.message ?? undefined },
      blocks,
      updateFlushedTask,
    );
  }
}

function absorbSubagentEvent(
  event: CodexTimelineEvent,
  eventParams: Record<string, unknown>,
  taskId: string,
  blocks: BlockManager,
  updateFlushedTask?: UpdateFlushedTask,
): void {
  if (isCodexItemStartedEvent(event)) {
    const item = eventParams.item as ThreadItem | undefined;
    if (
      item?.type === 'commandExecution' ||
      item?.type === 'fileChange' ||
      item?.type === 'mcpToolCall' ||
      item?.type === 'webSearch'
    ) {
      const state = blocks.codexSubagentsRef.current.get(taskId);
      const toolUses = (state?.toolUses ?? 0) + 1;
      if (state) state.toolUses = toolUses;
      updateSubagentTask(taskId, { status: 'in_progress', toolUses }, blocks, updateFlushedTask);
    }
    return;
  }

  if (isCodexItemCompletedEvent(event)) {
    const item = eventParams.item as ThreadItem | undefined;
    if (item?.type === 'agentMessage' && item.phase === 'final_answer' && item.text) {
      updateSubagentTask(taskId, { summary: item.text }, blocks, updateFlushedTask);
    }
    return;
  }

  if (isCodexErrorEvent(event)) {
    const error = asRecord(eventParams.error);
    updateSubagentTask(
      taskId,
      {
        status: 'failed',
        summary:
          typeof error?.message === 'string'
            ? error.message
            : typeof eventParams.message === 'string'
              ? eventParams.message
              : 'The background agent failed.',
      },
      blocks,
      updateFlushedTask,
    );
    return;
  }

  if (isCodexTurnCompletedEvent(event)) {
    const turn = asRecord(eventParams.turn);
    const status = typeof turn?.status === 'string' ? turn.status : 'completed';
    updateSubagentTask(
      taskId,
      {
        status: status === 'completed' ? 'completed' : 'failed',
        durationMs: typeof turn?.durationMs === 'number' ? turn.durationMs : undefined,
      },
      blocks,
      updateFlushedTask,
    );
    blocks.codexSubagentTerminalsRef.current += 1;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function params(data: Record<string, unknown>): Record<string, unknown> {
  return asRecord(data.params) ?? data;
}

function tokenCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

function parseTokenUsageBreakdown(value: unknown): ChatTokenUsage | null {
  const record = asRecord(value);
  if (!record) return null;
  return {
    totalTokens: tokenCount(record.totalTokens),
    inputTokens: tokenCount(record.inputTokens),
    cachedInputTokens: tokenCount(record.cachedInputTokens),
    cacheWriteInputTokens: tokenCount(record.cacheWriteInputTokens),
    outputTokens: tokenCount(record.outputTokens),
  };
}

/**
 * Per-turn spend from thread-cumulative counters: totals minus the previous
 * turn's baseline. Totals below the baseline mean the counters reset (the
 * Codex process restarted mid-thread), in which case the reported totals are
 * the turn's spend in full.
 */
function diffTokenUsage(total: ChatTokenUsage, baseline: ChatTokenUsage | null): ChatTokenUsage {
  if (!baseline || total.totalTokens < baseline.totalTokens) return total;
  return {
    totalTokens: Math.max(0, total.totalTokens - baseline.totalTokens),
    inputTokens: Math.max(0, total.inputTokens - baseline.inputTokens),
    cachedInputTokens: Math.max(0, total.cachedInputTokens - baseline.cachedInputTokens),
    cacheWriteInputTokens: Math.max(
      0,
      (total.cacheWriteInputTokens ?? 0) - (baseline.cacheWriteInputTokens ?? 0),
    ),
    outputTokens: Math.max(0, total.outputTokens - baseline.outputTokens),
  };
}

/**
 * Adapt Reflex's classified event shape to the SDK timeline guard surface.
 * The SDK guards own method classification; this adapter only preserves the
 * raw JSON-RPC frame and source event used by the chat block model.
 */
function toCodexTimelineEvent(
  data: Record<string, unknown>,
  sourceEvent: AxonEvent,
): CodexTimelineEvent {
  const axonEvent: AxonEventView = {
    axon_id: sourceEvent.streamId,
    event_type: sourceEvent.type,
    origin: sourceEvent.origin ?? 'EXTERNAL_EVENT',
    payload: JSON.stringify(data),
    sequence: sourceEvent.sequence ?? 0,
    source: sourceEvent.source ?? '',
    timestamp_ms: sourceEvent.timestamp,
  };
  return classifyCodexAxonEvent(axonEvent);
}

function statusOf(item: CodexToolItem, completed: boolean): ToolCallStatus {
  if (!completed) return 'in_progress';
  if (item.type === 'webSearch') return 'completed';
  if (item.status === 'failed') return 'failed';
  if (item.status === 'declined') return 'cancelled';
  return item.status === 'completed' ? 'completed' : 'in_progress';
}

function stringify(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return null;
  }
}

function toolPresentation(
  item: CodexToolItem,
): Pick<ToolCallBlock, 'title' | 'kind' | 'locations' | 'content' | 'rawInput' | 'rawOutput'> {
  switch (item.type) {
    case 'commandExecution': {
      const output = item.aggregatedOutput;
      return {
        title: item.command,
        kind: 'execute',
        locations: [],
        content: output ? [{ type: 'content', text: output }] : [],
        rawInput: { command: item.command, cwd: item.cwd },
        rawOutput:
          item.exitCode !== null || output !== null
            ? { exitCode: item.exitCode, output: output ?? '' }
            : null,
      };
    }
    case 'fileChange':
      return {
        title: item.changes.length === 1 ? `Update ${item.changes[0].path}` : 'Update files',
        kind: 'edit',
        locations: item.changes.map((change) => ({ path: change.path })),
        content: item.changes.map(
          (change): ContentItem => ({ type: 'content', text: change.diff }),
        ),
        rawInput: { changes: item.changes },
        rawOutput: null,
      };
    case 'mcpToolCall': {
      const result = stringify(item.result);
      const error = item.error?.message ?? null;
      return {
        title: `${item.server}: ${item.tool}`,
        kind: inferToolKind(item.tool),
        locations: [],
        content: result || error ? [{ type: 'content', text: error ?? result ?? '' }] : [],
        rawInput: asRecord(item.arguments),
        rawOutput: item.result || item.error ? { result: item.result, error: item.error } : null,
      };
    }
    case 'webSearch':
      return {
        title: 'Web search',
        kind: 'search',
        locations: [],
        content: [],
        rawInput: { query: item.query, action: item.action },
        rawOutput: null,
      };
  }
}

function upsertTool(
  item: CodexToolItem,
  completed: boolean,
  blocks: BlockManager,
  sourceEvent: AxonEvent,
  updateFlushedToolCall?: UpdateFlushedToolCall,
): void {
  blocks.finalizeThinking(sourceEvent.timestamp);
  const presentation = toolPresentation(item);
  const existing = blocks.blocksRef.current.find(
    (block): block is ToolCallBlock => block.type === 'tool_call' && block.toolCallId === item.id,
  );
  const status = statusOf(item, completed);

  if (existing) {
    blocks.updateBlocks((current) =>
      current.map((block) =>
        block.type === 'tool_call' && block.toolCallId === item.id
          ? {
              ...block,
              ...presentation,
              content: presentation.content.length > 0 ? presentation.content : block.content,
              rawInput: presentation.rawInput ?? block.rawInput,
              rawOutput: presentation.rawOutput ?? block.rawOutput,
              status,
              duration:
                completed && block.status === 'in_progress'
                  ? durationSecs(block.startedAt, sourceEvent.timestamp)
                  : block.duration,
            }
          : block,
      ),
    );
    return;
  }

  // A terminal frame with no live block belongs to a turn whose buffer was
  // already flushed: either a mid-turn flush moved the started card into a
  // finished message, or the agent abandoned a long-running command whose
  // process exited after the turn completed. Route the result back to the
  // flushed card; never mount an orphan tool card outside its turn.
  if (completed) {
    updateFlushedToolCall?.(
      item.id,
      {
        status,
        ...(presentation.content.length > 0 ? { content: presentation.content } : {}),
        ...(presentation.rawOutput ? { rawOutput: presentation.rawOutput } : {}),
      },
      sourceEvent.timestamp,
    );
    return;
  }

  blocks.pushBlock({
    type: 'tool_call',
    id: nextBlockId('tc'),
    toolCallId: item.id,
    ...presentation,
    status,
    startedAt: sourceEvent.timestamp,
    duration: null,
    sourceEvent,
    extra: { codexItemType: item.type },
  });
}

function appendTextDelta(
  itemId: string,
  delta: string,
  blocks: BlockManager,
  sourceEvent: AxonEvent,
): void {
  blocks.finalizeThinking(sourceEvent.timestamp);
  const last = blocks.lastBlock();
  if (last?.type === 'text' && last.extra?.codexItemId === itemId) {
    blocks.updateBlocks((current) =>
      current.map((block) =>
        block.id === last.id && block.type === 'text'
          ? { ...block, text: block.text + delta }
          : block,
      ),
    );
    return;
  }
  blocks.pushBlock({
    type: 'text',
    id: nextBlockId('txt'),
    text: delta,
    sourceEvent,
    extra: { codexItemId: itemId },
  });
}

function appendReasoningDelta(
  itemId: string,
  delta: string,
  blocks: BlockManager,
  sourceEvent: AxonEvent,
): void {
  const last = blocks.lastBlock();
  if (last?.type === 'thinking' && last.extra?.codexItemId === itemId && last.isActive) {
    blocks.updateBlocks((current) =>
      current.map((block) =>
        block.id === last.id && block.type === 'thinking'
          ? { ...block, text: block.text + delta }
          : block,
      ),
    );
    return;
  }
  blocks.thinkingStartRef.current ??= sourceEvent.timestamp;
  blocks.pushBlock({
    type: 'thinking',
    id: nextBlockId('think'),
    text: delta,
    duration: null,
    isActive: true,
    sourceEvent,
    extra: { codexItemId: itemId },
  });
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Map a `thread/start` JSON-RPC result into the session-info shape behind the
 * avatar popover. The result is the only frame in a native Codex stream that
 * carries model / effort / sandbox / approval-policy metadata.
 */
function parseCodexSessionInit(
  result: Record<string, unknown>,
): { extensions: CodexInitExtensions; model: string | null } | null {
  const thread = asRecord(result.thread);
  const extensions: CodexInitExtensions = {
    protocol: 'codex',
    modelProvider: optionalString(result.modelProvider),
    reasoningEffort: optionalString(result.reasoningEffort),
    approvalPolicy: optionalString(result.approvalPolicy),
    sandboxMode: optionalString(asRecord(result.sandbox)?.type),
    cwd: optionalString(result.cwd) ?? optionalString(thread?.cwd),
    cliVersion: optionalString(thread?.cliVersion),
    sessionId: optionalString(thread?.sessionId) ?? optionalString(thread?.id),
  };
  const model = optionalString(result.model) ?? null;
  // A result with no recognizable fields is not a session payload — emit
  // nothing rather than an empty popover.
  if (!model && Object.values(extensions).every((v) => v === undefined || v === 'codex')) {
    return null;
  }
  return { extensions, model };
}

function approvalTitle(method: string): string {
  switch (method) {
    case 'item/commandExecution/requestApproval':
    case 'execCommandApproval':
      return 'Command approval requested';
    case 'item/fileChange/requestApproval':
    case 'applyPatchApproval':
      return 'File change approval requested';
    case 'item/tool/requestUserInput':
    case 'mcpServer/elicitation/request':
      return 'Input requested';
    case 'item/permissions/requestApproval':
      return 'Permission approval requested';
    default:
      return 'Approval requested';
  }
}

function approvalDetail(method: string, requestParams: Record<string, unknown>): string | null {
  if (typeof requestParams.reason === 'string') return requestParams.reason;
  if (typeof requestParams.command === 'string') return requestParams.command;
  if (Array.isArray(requestParams.command)) return requestParams.command.join(' ');
  if (method === 'mcpServer/elicitation/request' && typeof requestParams.message === 'string') {
    return requestParams.message;
  }
  if (method === 'item/tool/requestUserInput') {
    const questions = requestParams.questions;
    if (Array.isArray(questions)) {
      const first = asRecord(questions[0]);
      if (typeof first?.question === 'string') return first.question;
    }
  }
  return null;
}

/**
 * Slash commands the Codex server provider accepts (`slash-commands.ts` in
 * `plugin-agent-codex`). The provider intercepts these before they reach the
 * model, so the list is static rather than agent-announced — keep names and
 * descriptions in sync with the server's `CODEX_SLASH_COMMANDS`.
 */
export const CODEX_AVAILABLE_COMMANDS: AvailableCommand[] = [
  { name: 'compact', description: 'Summarize the conversation to free context' },
  { name: 'review', description: 'Review current changes (optional instructions)' },
  { name: 'plan', description: 'Enter plan mode' },
  { name: 'default', description: 'Exit plan mode' },
  { name: 'model', description: 'Set the model for subsequent turns' },
  { name: 'effort', description: 'Set reasoning effort (minimal|low|medium|high|xhigh)' },
  { name: 'fast', description: 'Use the fast service tier (/fast off to reset)' },
  { name: 'status', description: 'Show model, mode, and rate limits' },
  { name: 'usage', description: 'Show account token usage' },
  { name: 'skills', description: 'List available skills' },
  { name: 'goal', description: 'Show, set, or clear the thread goal' },
  { name: 'help', description: 'List available commands' },
];

/** Whether text is a complete native Codex slash command handled by the provider. */
export function isCodexSlashCommand(text: string): boolean {
  const match = /^\/([a-z][a-z-]*)(?:\s+[\s\S]*)?$/.exec(text.trim());
  return match !== null && CODEX_AVAILABLE_COMMANDS.some((command) => command.name === match[1]);
}

/** Render native Codex app-server frames into the shared chat block model. */
export function handleCodexProtocolEvent(
  data: Record<string, unknown>,
  blocks: BlockManager,
  appendMessage: (msg: ChatItem) => void,
  setIsAgentTurn: (value: boolean) => void,
  sourceEvent: AxonEvent,
  setAvailableCommands?: (cmds: AvailableCommand[]) => void,
  updateFlushedTask?: UpdateFlushedTask,
  updateFlushedToolCall?: UpdateFlushedToolCall,
): void {
  const eventParams = params(data);

  // Codex slash commands are a static provider-side list, so any codex frame
  // is the signal to enable composer autocomplete. The module-level constant
  // keeps the reference stable, so repeat calls bail out of a re-render.
  setAvailableCommands?.(CODEX_AVAILABLE_COMMANDS);

  // Token-usage frames are tracked, not rendered: the cumulative totals feed
  // the finished turn's usage footer at the next turn boundary. Handled before
  // the SDK timeline adapter because the SDK's guard surface does not classify
  // this method.
  if (data.type === CODEX_TOKEN_USAGE_EVENT_TYPE) {
    const total = parseTokenUsageBreakdown(asRecord(eventParams.tokenUsage)?.total);
    if (total) {
      blocks.codexTokenUsageRef.current = {
        total,
        turnBaseline: blocks.codexTokenUsageRef.current?.turnBaseline ?? null,
      };
    }
    return;
  }

  // Slash-command results are reflex-domain durable events, not JSON-RPC
  // frames: the publish payload is spread directly into `data` (no `params`
  // envelope). Handled before the SDK timeline adapter, which does not
  // classify this type.
  if (data.type === CODEX_COMMAND_RESULT_EVENT_TYPE) {
    const block = {
      type: 'codex_command_result',
      id: nextBlockId('codex-cmd'),
      command: typeof data.command === 'string' ? data.command : '',
      status: data.status === 'error' ? 'error' : 'ok',
      message: typeof data.message === 'string' ? data.message : '',
      sourceEvent,
      extra: { overrides: data.overrides },
    } as const;
    appendMessage({
      id: `codex-command-${sourceEvent.id}`,
      role: 'system',
      itemType: 'codex_command_result',
      block,
    });
    return;
  }

  // Outbound `thread/start` request: record its JSON-RPC id so the paired
  // response (the only frame carrying session metadata) can be recognized.
  if (data.type === CODEX_THREAD_START_EVENT_TYPE && data.id != null) {
    blocks.codexSessionInitRef.current.requestMethods.set(
      String(data.id),
      CODEX_THREAD_START_EVENT_TYPE,
    );
    return;
  }

  // JSON-RPC `response` frames resolve an earlier server request: either the
  // SDK auto-approving an approval or the parked `item/tool/requestUserInput`
  // answer supplied through the chat. Mark the matching approval block
  // resolved so the question card collapses into its answered summary.
  if (data.type === 'response' && data.id != null) {
    const requestId = String(data.id);

    // Response paired to a tracked client request. Currently only
    // `thread/start` is tracked: parse its result into the pending
    // session-info payload, emitted as a `system_init` block at the next
    // `turn/started`. Error responses (e.g. the duplicate-handshake
    // `-32600` on resumed streams) carry no result and are dropped here.
    const init = blocks.codexSessionInitRef.current;
    const pairedMethod = init.requestMethods.get(requestId);
    if (pairedMethod != null) {
      init.requestMethods.delete(requestId);
      const result = asRecord(data.result);
      if (pairedMethod === CODEX_THREAD_START_EVENT_TYPE && result) {
        const parsed = parseCodexSessionInit(result);
        const sessionId = parsed?.extensions.sessionId;
        if (parsed && !(sessionId && init.emittedSessionIds.has(sessionId))) {
          init.pending = parsed;
        }
      }
      return;
    }
    const result = asRecord(data.result);
    blocks.updateBlocks((current) =>
      current.map((block) =>
        block.type === 'codex_approval' && block.requestId === requestId
          ? { ...block, responded: true, ...(result ? { result } : {}) }
          : block,
      ),
    );
    return;
  }

  const event = toCodexTimelineEvent(data, sourceEvent);

  // Codex publishes collaboration lifecycle as ordinary typed ThreadItems.
  // Handle those before the general item mapping so a child thread becomes a
  // single task card instead of leaking its messages and tools into the parent
  // conversation.
  if (isCodexItemStartedEvent(event) || isCodexItemCompletedEvent(event)) {
    const item = eventParams.item as ThreadItem | undefined;
    if (item?.type === 'subAgentActivity') {
      handleSubAgentActivity(item, blocks, sourceEvent, updateFlushedTask);
      return;
    }
    if (item?.type === 'collabAgentToolCall') {
      handleCollabAgentToolCall(item, blocks, sourceEvent, updateFlushedTask);
      return;
    }
  }

  const eventThreadId = optionalString(eventParams.threadId);
  const subagent = eventThreadId ? blocks.codexSubagentsRef.current.get(eventThreadId) : undefined;
  if (subagent) {
    absorbSubagentEvent(event, eventParams, subagent.taskId, blocks, updateFlushedTask);
    return;
  }

  // Emit the session-info block at the first `turn/started` after the
  // `thread/start` response was parsed — not at parse time, which precedes
  // the first turn and would mount an empty live card. Joining the turn's
  // block buffer mirrors Claude's `system` init: AssistantTurn filters it
  // from the visible body and surfaces it as the avatar popover.
  if (isCodexTurnStartedEvent(event)) {
    const init = blocks.codexSessionInitRef.current;
    if (init.pending) {
      const { extensions, model } = init.pending;
      init.pending = null;
      if (extensions.sessionId) init.emittedSessionIds.add(extensions.sessionId);
      blocks.pushBlock({
        type: 'system_init',
        id: nextBlockId('init'),
        agentName: 'Codex',
        agentVersion: extensions.cliVersion ?? null,
        model,
        commands: CODEX_AVAILABLE_COMMANDS.map((command) => command.name),
        extensions,
        sourceEvent,
      });
    }
    return;
  }

  if (isCodexAgentMessageDeltaEvent(event)) {
    setIsAgentTurn(true);
    appendTextDelta(
      String(eventParams.itemId ?? ''),
      typeof eventParams.delta === 'string' ? eventParams.delta : '',
      blocks,
      sourceEvent,
    );
    return;
  }

  if (isCodexReasoningSummaryTextDeltaEvent(event) || isCodexReasoningTextDeltaEvent(event)) {
    setIsAgentTurn(true);
    appendReasoningDelta(
      String(eventParams.itemId ?? ''),
      typeof eventParams.delta === 'string' ? eventParams.delta : '',
      blocks,
      sourceEvent,
    );
    return;
  }

  if (isCodexReasoningSummaryPartAddedEvent(event)) {
    setIsAgentTurn(true);
    const last = blocks.lastBlock();
    const separator =
      last?.type === 'thinking' && last.extra?.codexItemId === eventParams.itemId && last.text
        ? '\n'
        : '';
    appendReasoningDelta(String(eventParams.itemId ?? ''), separator, blocks, sourceEvent);
    return;
  }

  if (isCodexCommandExecutionOutputDeltaEvent(event)) {
    setIsAgentTurn(true);
    const itemId = String(eventParams.itemId ?? '');
    const delta = typeof eventParams.delta === 'string' ? eventParams.delta : '';
    blocks.updateBlocks((current) =>
      current.map((block) => {
        if (block.type !== 'tool_call' || block.toolCallId !== itemId) return block;
        const currentText = block.content.find((item) => item.type === 'content')?.text ?? '';
        return {
          ...block,
          content: [{ type: 'content', text: currentText + delta }],
          rawOutput: { ...(block.rawOutput ?? {}), output: currentText + delta },
        };
      }),
    );
    // `item/started` is ordered before output deltas by app-server. Do not
    // synthesize an orphan card without the command/cwd metadata needed to
    // label it; a later replay will restore the preceding start event.
    return;
  }

  if (isCodexErrorEvent(event)) {
    setIsAgentTurn(true);
    const error = asRecord(eventParams.error);
    blocks.pushBlock({
      type: 'codex_error',
      id: nextBlockId('codex-error'),
      message:
        typeof error?.message === 'string'
          ? error.message
          : typeof eventParams.message === 'string'
            ? eventParams.message
            : 'Codex encountered an error.',
      willRetry: eventParams.willRetry === true,
      sourceEvent,
      extra: { params: eventParams },
    });
    return;
  }

  if (isCodexItemStartedEvent(event) || isCodexItemCompletedEvent(event)) {
    const item = eventParams.item as ThreadItem | undefined;
    if (!item) return;
    const completed = isCodexItemCompletedEvent(event);
    if (
      item.type === 'commandExecution' ||
      item.type === 'fileChange' ||
      item.type === 'mcpToolCall' ||
      item.type === 'webSearch'
    ) {
      // A tool item settling does not end the turn; only the turn terminal
      // owns that transition. In particular, a late completion from an older
      // turn must not mark a newer, not-yet-rendering turn idle.
      if (!completed || blocks.blocksRef.current.length > 0) setIsAgentTurn(true);
      upsertTool(item, completed, blocks, sourceEvent, updateFlushedToolCall);
      return;
    }
    if (item.type === 'agentMessage' && completed) {
      // A mid-turn flush (steer, slash command) may have already moved part
      // of this item's streamed text into a finished message. The terminal
      // frame carries the full text, so reconcile only the unflushed
      // remainder against the live buffer.
      const flushedPrefix = blocks.codexFlushedItemTextRef.current.get(item.id) ?? '';
      const itemText =
        flushedPrefix && item.text.startsWith(flushedPrefix)
          ? item.text.slice(flushedPrefix.length)
          : item.text;
      const matching = blocks.blocksRef.current.filter(
        (block): block is Extract<(typeof blocks.blocksRef.current)[number], { type: 'text' }> =>
          block.type === 'text' && block.extra?.codexItemId === item.id,
      );
      if (matching.length > 0 && itemText) {
        const streamedText = matching.map((block) => block.text).join('');
        if (streamedText !== itemText) {
          const prefix = matching
            .slice(0, -1)
            .map((block) => block.text)
            .join('');
          const last = matching[matching.length - 1];
          blocks.updateBlocks((current) =>
            current.map((block) => {
              if (block.type !== 'text' || block.extra?.codexItemId !== item.id) return block;
              if (itemText.startsWith(prefix)) {
                return block.id === last.id
                  ? { ...block, text: itemText.slice(prefix.length) }
                  : block;
              }
              return block.id === matching[0].id
                ? { ...block, text: itemText }
                : { ...block, text: '' };
            }),
          );
        }
      } else if (itemText) {
        appendTextDelta(item.id, itemText, blocks, sourceEvent);
      }
      return;
    }
    if (item.type === 'reasoning' && completed) {
      const existing = blocks.blocksRef.current.find(
        (block) => block.type === 'thinking' && block.extra?.codexItemId === item.id,
      );
      const alreadyFlushed = blocks.codexFlushedItemTextRef.current.has(item.id);
      if (!existing && !alreadyFlushed) {
        const text = [...item.summary, ...item.content].join('\n');
        if (text) appendReasoningDelta(item.id, text, blocks, sourceEvent);
      }
      blocks.finalizeThinking(sourceEvent.timestamp);
    }
    // Other ThreadItem variants (including plan and userMessage) have no
    // Phase 3 chat-block mapping. User prompts are rendered from the durable
    // outbound turn/start frame before Codex echoes a userMessage item.
    return;
  }

  if (isCodexApprovalRequestEvent(event)) {
    setIsAgentTurn(true);
    const requestId = String(data.id ?? `${sourceEvent.id}`);
    blocks.pushBlock({
      type: 'codex_approval',
      id: nextBlockId('approval'),
      requestId,
      method: sourceEvent.type,
      title: approvalTitle(sourceEvent.type),
      detail: approvalDetail(sourceEvent.type, eventParams),
      sourceEvent,
      // rpcId keeps the wire-typed JSON-RPC id (number or string) so an
      // answer can echo it back exactly; requestId is its string form.
      extra: { params: eventParams, rpcId: data.id },
    });
    return;
  }

  if (isCodexTurnCompletedEvent(event)) {
    blocks.finalizeThinking(sourceEvent.timestamp);
    const turn = asRecord(eventParams.turn);
    const status = typeof turn?.status === 'string' ? turn.status : undefined;
    const usageState = blocks.codexTokenUsageRef.current;
    const turnUsage = usageState ? diffTokenUsage(usageState.total, usageState.turnBaseline) : null;
    if (usageState) {
      blocks.codexTokenUsageRef.current = {
        total: usageState.total,
        turnBaseline: usageState.total,
      };
    }
    const flushed = blocks.flushToMessage(
      {
        stopReason: status === 'completed' ? undefined : status,
        isError: status === 'failed',
        durationMs: typeof turn?.durationMs === 'number' ? turn.durationMs : undefined,
        ...(turnUsage && turnUsage.totalTokens > 0 ? { tokenUsage: turnUsage } : {}),
      },
      sourceEvent.timestamp,
    );
    if (flushed) appendMessage(flushed);
    // Item ids are unique per item, but the flushed-text map only matters
    // within the turn whose terminal frames replay full item text.
    blocks.codexFlushedItemTextRef.current = new Map();
    setIsAgentTurn(false);
  }
}
